(() => {
  'use strict';
  const root = document.documentElement;
  const canvas = document.querySelector('[data-dx-cosmos]');
  if (!canvas) return;
  const universe = canvas.closest('.dx-universe');
  const exploreButton = document.querySelector('[data-dx-explore]');
  const help = document.querySelector('[data-dx-help]');
  const worldButtons = [...document.querySelectorAll('[data-dx-world]')];
  const labels = document.querySelectorAll('[data-dx-planet-name]');
  const sections = [...document.querySelectorAll('[data-dx-section]')];
  const navLinks = [...document.querySelectorAll('[data-dx-nav]')];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const pointerFine = matchMedia('(pointer:fine)');
  let paused = root.dataset.obMotion === 'paused';
  let exploring = false;
  let activeSection = 'arrival';
  let manualWorld = null;
  let targetWorld = 0;
  let frameId = 0;
  let dirty = true;
  let lastFrame = 0;
  let elapsed = 0;
  let yaw = -.7;
  let pitch = .12;
  let drag = null;
  let lastFocus = null;
  let savedOverflow = '';
  let loaded = 0;
  let gl;
  let gradientSampling = false;
  const showFallback = () => {
    root.classList.remove('dx-scene-ready');
    root.classList.add('dx-scene-failed');
    if (exploreButton) exploreButton.hidden = true;
  };
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const mix = (a, b, t) => a + (b - a) * t;
  const ease = t => t * t * (3 - 2 * t);
  const pointer = { x: 0, y: 0 };
  const smoothPointer = { x: 0, y: 0 };
  const scene = { x: .53, y: -.03, radius: .52, world: 0, turn: 0 };
  let desired = { ...scene };
  let positions = [];
  const hiddenSurfaces = [document.querySelector('.dx-header'), document.querySelector('#ob-main'), document.querySelector('.dx-footer'), document.querySelector('#ob-mobile-nav')].filter(Boolean);
  const inertStates = new Map();
  const vertexSource = 'attribute vec2 aPosition;void main(){gl_Position=vec4(aPosition,0.0,1.0);}';
  const fragmentSource = `
    precision highp float;
    uniform vec2 uResolution;
    uniform vec4 uPlanet;
    uniform vec3 uRotation;
    uniform vec2 uPointer;
    uniform float uTime;
    uniform float uJourney;
    uniform sampler2D uEarth;
    uniform sampler2D uMars;
    uniform sampler2D uClouds;
    const float PI=3.14159265359;
    float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
    float noise(vec2 p){vec2 i=floor(p);vec2 f=fract(p);f=f*f*(3.0-2.0*f);return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.)),f.x),f.y);}
    float cloud(vec2 p){return noise(p)*.57+noise(p*2.01)*.28+noise(p*4.03)*.15;}
    mat2 rot(float a){return mat2(cos(a),-sin(a),sin(a),cos(a));}
    vec3 stars(vec2 uv,float scale,float shift){
      vec2 p=uv*scale+vec2(shift,0.);vec2 cell=floor(p);vec2 f=fract(p)-.5;
      float rnd=hash(cell);vec2 off=vec2(hash(cell+3.1),hash(cell+5.7))-.5;
      float dist=length(f-off*.7);float radius=mix(.014,.045,rnd);
      float v=(1.-smoothstep(0.,radius,dist))*step(.91,rnd);
      float halo=exp(-dist*34.)*.15*step(.985,rnd);
      return mix(vec3(.42,.67,.78),vec3(1.,.88,.67),hash(cell+13.))*(v+halo);
    }
    void main(){
      vec2 uv=(gl_FragCoord.xy-.5*uResolution)/uResolution.y;
      float aspect=uResolution.x/uResolution.y;
      vec2 shifted=uv+uPointer*.013+vec2(uJourney*.035,uJourney*-.018);
      float fog=cloud(shifted*3.+vec2(uTime*.001,0.));
      float nebula=exp(-pow((shifted.y+shifted.x*.38-.1)*2.8,2.));
      vec3 col=vec3(.018,.024,.029);
      col+=vec3(.018,.075,.087)*nebula*fog*.68;
      col+=vec3(.057,.028,.008)*exp(-length(shifted-vec2(-.8,.25))*1.9)*fog*.6;
      col+=stars(shifted,58.,1.7)*.8;
      col+=stars(shifted+vec2(.03,-.04)*uTime*.001,103.,6.9)*.34;
      vec2 centre=uPlanet.xy+uPointer*.014;
      float radius=uPlanet.z;
      float world=uPlanet.w;
      vec2 p=(uv-centre)/radius;
      float d=length(p);
      vec3 atmosphere=mix(vec3(.13,.61,.92),vec3(.92,.38,.12),world);
      float outer=exp(-max(d-1.,0.)*28.)*.22*step(1.,d);
      outer+=exp(-max(d-1.,0.)*7.)*.032*step(1.,d);
      col+=atmosphere*outer;
      vec2 orbit=rot(-.39+uJourney*.025)*(uv-centre);
      float oval=length(orbit/vec2(radius*1.5,radius*.46));
      float orbitLine=exp(-abs(oval-1.)*520.);
      float orbitBack=step(0.,orbit.y);
      col+=mix(vec3(.1,.43,.5),vec3(.77,.42,.12),world)*orbitLine*.21*orbitBack;
      {
        vec3 normal=vec3(p,sqrt(max(0.,1.-d*d)));
        vec3 n=normal;
        n.yz=rot(uRotation.y)*n.yz;
        n.xz=rot(uRotation.x+uRotation.z)*n.xz;
        vec2 tex=vec2(-atan(n.z,n.x)/(2.*PI)+.5,asin(clamp(n.y,-1.,1.))/PI+.5);
        #ifdef DX_GRADIENTS
        vec2 uvDx=dFdx(tex);
        vec2 uvDy=dFdy(tex);
        uvDx.x-=floor(uvDx.x+.5);
        uvDy.x-=floor(uvDy.x+.5);
        #endif
        if(d<1.){
        #ifdef DX_GRADIENTS
        vec3 earth=texture2DGradEXT(uEarth,vec2(fract(tex.x),tex.y),uvDx,uvDy).rgb;
        vec3 mars=texture2DGradEXT(uMars,vec2(fract(tex.x),tex.y),uvDx,uvDy).rgb;
        float clouds=texture2DGradEXT(uClouds,vec2(fract(tex.x+uTime*.0007),tex.y),uvDx,uvDy).r;
        #else
        vec3 earth=texture2D(uEarth,vec2(fract(tex.x),tex.y)).rgb;
        vec3 mars=texture2D(uMars,vec2(fract(tex.x),tex.y)).rgb;
        float clouds=texture2D(uClouds,vec2(fract(tex.x+uTime*.0007),tex.y)).r;
        #endif
        vec3 surface=mix(earth,mars,world);
        vec3 light=normalize(vec3(-.8,.55,1.1));
        float diffuse=max(0.,dot(normal,light));
        float terminator=smoothstep(-.12,.4,dot(normal,light));
        surface*=.075+diffuse*.92;
        surface=mix(surface,vec3(.84,.9,.94)*(.12+.88*diffuse),clouds*.67*(1.-world));
        float water=clamp((earth.b-earth.r)*4.,0.,1.);
        float shine=pow(max(dot(reflect(-light,normal),vec3(0.,0.,1.)),0.),30.);
        surface+=vec3(.28,.46,.53)*shine*water*(1.-world)*.3;
        float rim=pow(1.-normal.z,3.6)*(.18+.82*terminator);
        surface+=atmosphere*rim*.68;
        surface+=atmosphere*pow(1.-normal.z,1.8)*.035;
        float edge=1.-smoothstep(.997,1.,d);
        col=mix(col,surface,edge);
        }
      }
      col+=mix(vec3(.12,.5,.61),vec3(.88,.46,.13),world)*orbitLine*.17*(1.-orbitBack);
      vec2 starpos=centre+rot(.39-uJourney*.025)*vec2(cos(uTime*.015+2.)*radius*1.5,sin(uTime*.015+2.)*radius*.46);
      float orb=length(uv-starpos);
      col+=vec3(.42,.8,.92)*exp(-orb*950.)*.8;
      col+=vec3(.12,.48,.64)*exp(-orb*130.)*.09;
      float vignette=1.-smoothstep(.4,1.4,length(uv/vec2(max(aspect,.8),1.)))*.28;
      col*=vignette;
      col=pow(max(col,vec3(0.)),vec3(.94));
      gl_FragColor=vec4(col,1.);
    }`;

  const setWorldLabel = value => {
    const name = value > .5 ? 'mars' : 'earth';
    worldButtons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.dxWorld === name)));
    const label = worldButtons.find(b => b.dataset.dxWorld === name)?.textContent;
    labels.forEach(n => { if (n.textContent !== label) n.textContent = label; });
  };
  function measure() {
    positions = sections.map(s => ({ name: s.dataset.dxSection, top: s.getBoundingClientRect().top + scrollY }));
    resize();
    updateScroll();
  }
  function updateScroll() {
    const mobile = innerWidth < 700;
    const keys = mobile
      ? [{x:.03,y:-.23,radius:.36,world:0,turn:0},{x:.18,y:.04,radius:.47,world:0,turn:.7},{x:-.13,y:-.1,radius:.46,world:1,turn:1.1},{x:.16,y:.18,radius:.37,world:1,turn:1.5},{x:0,y:.12,radius:.38,world:0,turn:2.1}]
      : [{x:.53,y:-.03,radius:.52,world:0,turn:0},{x:.37,y:.0,radius:.6,world:0,turn:.5},{x:.58,y:-.02,radius:.54,world:1,turn:1.1},{x:-.35,y:.06,radius:.49,world:1,turn:1.6},{x:0,y:.18,radius:.5,world:0,turn:2.2}];
    let index = 0;
    let next = 0;
    let t = 0;
    const probe = scrollY + innerHeight * .28;
    if (positions.length) {
      for (let i = 0; i < positions.length; i++) if (probe >= positions[i].top) index = i;
      next = Math.min(index + 1, positions.length - 1);
      if (next !== index) t = clamp((probe - positions[index].top) / (positions[next].top - positions[index].top), 0, 1);
      const name = positions[index].name;
      if (name !== activeSection) {
        activeSection = name;
        manualWorld = null;
        navLinks.forEach(l => {
          if (l.dataset.dxNav === name) l.setAttribute('aria-current', 'location');
          else l.removeAttribute('aria-current');
        });
      }
      const a = keys[index] || keys[0];
      const b = keys[next] || a;
      for (const k of Object.keys(a)) desired[k] = mix(a[k], b[k], ease(t));
    } else desired = mobile ? {x:.14,y:.08,radius:.5,world:0,turn:.4} : {x:.64,y:.02,radius:.63,world:0,turn:.5};
    targetWorld = manualWorld === null ? desired.world : manualWorld;
    setWorldLabel(targetWorld);
    const copy = document.querySelector('.dx-hero-copy');
    if (copy && !paused && !reduced.matches) copy.style.setProperty('--dx-copy-y', `${Math.min(scrollY, innerHeight) * .10}px`);
    dirty = true;
    wake();
  }
  function resize() {
    if (!gl) return;
    const limit = innerWidth < 700 ? 1.75 : 1.5;
    const dpr = Math.min(devicePixelRatio || 1, limit);
    const scale = Math.min(1, 2400 / (innerWidth * dpr), Math.sqrt(2800000 / (innerWidth * innerHeight * dpr * dpr)));
    canvas.width = Math.round(innerWidth * dpr * scale);
    canvas.height = Math.round(innerHeight * dpr * scale);
    gl.viewport(0, 0, canvas.width, canvas.height);
    dirty = true;
  }
  let program;
  const uniforms = {};
  function shader(type, source) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, source);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh));
    return sh;
  }
  function loadTexture(name, unit, source, highSource) {
    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([25, 42, 49, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(gl.getUniformLocation(program, name), unit);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    let usingHigh = Boolean(highSource && gl.getParameter(gl.MAX_TEXTURE_SIZE) >= 4096);
    const fallback = () => {
      if (usingHigh) { usingHigh = false; img.src = source; }
      else showFallback();
    };
    img.onload = () => {
      if (!gl || gl.isContextLost()) return;
      if (img.width > Math.min(4096, gl.getParameter(gl.MAX_TEXTURE_SIZE))) { fallback(); return; }
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      if ((img.width & (img.width - 1)) === 0 && (img.height & (img.height - 1)) === 0) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        if (gradientSampling) {
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        const anisotropy = gl.getExtension('EXT_texture_filter_anisotropic');
        if (anisotropy) gl.texParameterf(gl.TEXTURE_2D, anisotropy.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(4, gl.getParameter(anisotropy.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      }
      }
      canvas.dataset[`texture${name.slice(1)}`] = `${img.width}x${img.height}`;
      loaded++;
      dirty = true;
      wake();
    };
    img.onerror = fallback;
    img.src = usingHigh ? highSource : source;
  }
  function init() {
    loaded = 0;
    root.classList.remove('dx-scene-ready', 'dx-scene-failed');
    try {
      gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power', preserveDrawingBuffer: false });
      if (!gl) throw new Error('WebGL unavailable');
      gradientSampling = Boolean(gl.getExtension('OES_standard_derivatives') && gl.getExtension('EXT_shader_texture_lod'));
      canvas.dataset.textureFiltering = gradientSampling ? 'seam-safe-mipmaps' : 'linear';
      program = gl.createProgram();
      gl.attachShader(program, shader(gl.VERTEX_SHADER, vertexSource));
      gl.attachShader(program, shader(gl.FRAGMENT_SHADER, (gradientSampling ? '#extension GL_OES_standard_derivatives : enable\n#extension GL_EXT_shader_texture_lod : enable\n#define DX_GRADIENTS 1\n' : '') + fragmentSource));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
      gl.useProgram(program);
      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'aPosition');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      for (const key of ['uResolution','uPlanet','uRotation','uPointer','uTime','uJourney']) uniforms[key] = gl.getUniformLocation(program, key);
      loadTexture('uEarth', 0, canvas.dataset.earth, canvas.dataset.earthHd);
      loadTexture('uMars', 1, canvas.dataset.mars, canvas.dataset.marsHd);
      loadTexture('uClouds', 2, canvas.dataset.clouds, canvas.dataset.cloudsHd);
      measure();
    } catch (error) {
      gl = null;
      showFallback();
      console.warn('SpaceEve scene uses its static fallback:', error.message);
    }
  }
  function render(now) {
    frameId = 0;
    if (!gl || document.hidden) return;
    const moving = !paused && !reduced.matches;
    const interval = innerWidth < 700 ? 50 : 40;
    if (now - lastFrame < interval) { wake(); return; }
    const dt = Math.min((now - lastFrame) / 1000 || .04, .1);
    lastFrame = now;
    if (moving && !drag) elapsed += dt;
    const smoothing = moving ? 1 - Math.exp(-dt * 5) : 1;
    const target = exploring ? {x:0,y:.03,radius:innerWidth < 700 ? .36 : .43,world:targetWorld,turn:0} : {...desired,world:targetWorld};
    let settling = false;
    for (const key of Object.keys(scene)) {
      scene[key] = mix(scene[key], target[key], smoothing);
      if (Math.abs(scene[key] - target[key]) > .0003) settling = true;
    }
    smoothPointer.x = mix(smoothPointer.x, moving ? pointer.x : 0, .08);
    smoothPointer.y = mix(smoothPointer.y, moving ? pointer.y : 0, .08);
    gl.uniform2f(uniforms.uResolution, canvas.width, canvas.height);
    gl.uniform4f(uniforms.uPlanet, scene.x, scene.y, scene.radius, scene.world);
    gl.uniform3f(uniforms.uRotation, yaw + elapsed * .017, pitch, scene.turn);
    gl.uniform2f(uniforms.uPointer, smoothPointer.x, smoothPointer.y);
    gl.uniform1f(uniforms.uTime, elapsed);
    gl.uniform1f(uniforms.uJourney, scene.turn);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    if (loaded === 3 && !root.classList.contains('dx-scene-ready') && !root.classList.contains('dx-scene-failed')) root.classList.add('dx-scene-ready');
    const sceneName = exploring ? 'exploring' : activeSection;
    if (canvas.dataset.scene !== sceneName) canvas.dataset.scene = sceneName;
    dirty = false;
    if (moving || settling) wake();
  }
  function wake() { if (gl && !frameId && !document.hidden) frameId = requestAnimationFrame(render); }
  function setExploring(value) {
    if (!gl) return;
    exploring = value;
    root.classList.toggle('dx-exploring', value);
    exploreButton.setAttribute('aria-pressed', String(value));
    exploreButton.querySelector('span').textContent = value ? exploreButton.dataset.exit : exploreButton.dataset.enter;
    help.hidden = !value;
    if (value) {
      lastFocus = document.activeElement;
      savedOverflow = root.style.overflow;
      root.style.overflow = 'hidden';
      for (const element of hiddenSurfaces) { inertStates.set(element, element.inert); element.inert = true; }
      universe.removeAttribute('aria-hidden');
      canvas.setAttribute('tabindex', '0');
      canvas.setAttribute('role', 'img');
      canvas.setAttribute('aria-label', canvas.dataset.label || help.textContent);
      canvas.focus({ preventScroll: true });
    } else {
      root.style.overflow = savedOverflow;
      for (const element of hiddenSurfaces) element.inert = inertStates.get(element) || false;
      universe.setAttribute('aria-hidden', 'true');
      canvas.removeAttribute('tabindex');
      (lastFocus || exploreButton).focus({ preventScroll: true });
      drag = null;
    }
    dirty = true;
    wake();
  }
  exploreButton?.addEventListener('click', () => setExploring(!exploring));
  worldButtons.forEach(button => button.addEventListener('click', () => {
    manualWorld = button.dataset.dxWorld === 'mars' ? 1 : 0;
    targetWorld = manualWorld;
    setWorldLabel(targetWorld);
    if (!gl || root.classList.contains('dx-scene-failed')) document.querySelector('.dx-scene-fallback').style.backgroundImage = `url("${canvas.dataset[button.dataset.dxWorld]}")`;
    dirty = true;
    wake();
  }));
  canvas.addEventListener('pointerdown', e => {
    if (!exploring) return;
    drag = { x: e.clientX, y: e.clientY };
    canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener('pointermove', e => {
    if (!drag || !exploring) return;
    yaw += (e.clientX - drag.x) * .008;
    pitch = clamp(pitch + (e.clientY - drag.y) * .006, -1.25, 1.25);
    drag.x = e.clientX; drag.y = e.clientY;
    dirty = true;
    wake();
  });
  const release = () => { drag = null; };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);
  canvas.addEventListener('lostpointercapture', release);
  document.addEventListener('keydown', e => {
    if (!exploring) return;
    if (e.key === 'Escape') { e.preventDefault(); setExploring(false); }
    if (e.target !== canvas) return;
    if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) {
      e.preventDefault();
      if (e.key === 'ArrowLeft') yaw -= .12;
      if (e.key === 'ArrowRight') yaw += .12;
      if (e.key === 'ArrowUp') pitch = clamp(pitch - .1, -1.25, 1.25);
      if (e.key === 'ArrowDown') pitch = clamp(pitch + .1, -1.25, 1.25);
      dirty = true;
      wake();
    }
  });
  addEventListener('pointermove', e => {
    if (!pointerFine.matches || paused || exploring) return;
    pointer.x = (e.clientX / innerWidth - .5) * 2;
    pointer.y = (.5 - e.clientY / innerHeight) * 2;
  }, { passive: true });
  document.querySelectorAll('[data-dx-depth]').forEach(card => {
    card.addEventListener('pointermove', e => {
      if (!pointerFine.matches || paused || reduced.matches) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty('--dx-tilt-x', `${(.5 - (e.clientY - r.top) / r.height) * 4}deg`);
      card.style.setProperty('--dx-tilt-y', `${((e.clientX - r.left) / r.width - .5) * 5}deg`);
    }, { passive: true });
    card.addEventListener('pointerleave', () => { card.style.removeProperty('--dx-tilt-x'); card.style.removeProperty('--dx-tilt-y'); });
  });
  if ('IntersectionObserver' in window) {
    const entrances = new IntersectionObserver(entries => {
      let order = 0;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!paused && !reduced.matches) {
          entry.target.style.setProperty('--dx-entry-delay', `${Math.min(order++, 3) * 90}ms`);
          entry.target.classList.add('dx-card-enter');
        }
        entrances.unobserve(entry.target);
      }
    }, { threshold: .08 });
    document.querySelectorAll('[data-dx-depth]').forEach(card => entrances.observe(card));
  }
  let scrollQueued = false;
  addEventListener('scroll', () => {
    if (scrollQueued || exploring) return;
    scrollQueued = true;
    requestAnimationFrame(() => { scrollQueued = false; updateScroll(); });
  }, { passive: true });
  let resizeTimer;
  addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(measure, 140); }, { passive: true });
  document.addEventListener('ob:motion', e => { paused = e.detail.paused; dirty = true; wake(); });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { cancelAnimationFrame(frameId); frameId = 0; }
    else { lastFrame = performance.now(); dirty = true; wake(); }
  });
  canvas.addEventListener('webglcontextlost', e => {
    e.preventDefault();
    cancelAnimationFrame(frameId); frameId = 0;
    if (exploring) setExploring(false);
    showFallback();
    gl = null;
  });
  canvas.addEventListener('webglcontextrestored', () => { loaded = 0; exploreButton.hidden = false; init(); });
  init();
  document.fonts?.ready.then(measure);
  addEventListener('load', measure, { once: true });
})();

